import { SceneLoader, TransformNode, type Vector3, type Scene } from "@babylonjs/core";
import "@babylonjs/loaders/glTF";

const MODEL_ROOT_URL = "/assets/models/";
const MODEL_FILE = "mermaid.glb";

/**
 * The rare mermaid-visit event's mermaid: a static (unanimated) CC-BY
 * sourced model ("Mermaid in a bubble" by Megan Lent — see
 * assets-pipeline/ASSET_SOURCING.md for the required attribution and why
 * it's specifically in a bubble rather than a bare figure). Hidden by
 * default; shown only while her vignette plays.
 *
 * Loading is still best-effort even though the asset is in place now: if
 * the glb is ever missing (e.g. a future re-export), this logs once and
 * every method becomes a safe no-op rather than breaking the rest of the
 * scene.
 */
export class MermaidVisitor {
  private root: TransformNode | null = null;

  static async load(scene: Scene, position: Vector3): Promise<MermaidVisitor> {
    const visitor = new MermaidVisitor();
    try {
      const result = await SceneLoader.ImportMeshAsync("", MODEL_ROOT_URL, MODEL_FILE, scene);
      const root = new TransformNode("mermaidRoot", scene);
      for (const mesh of result.meshes) {
        if (!mesh.parent) mesh.parent = root;
      }
      root.position = position;
      root.setEnabled(false);
      visitor.root = root;
    } catch (err) {
      console.warn(
        `[Castaway] MermaidVisitor: "${MODEL_FILE}" isn't available yet — the mermaid-visit vignette will no-op until it is.`,
        err
      );
    }
    return visitor;
  }

  isAvailable(): boolean {
    return this.root !== null;
  }

  show(): void {
    this.root?.setEnabled(true);
  }

  hide(): void {
    this.root?.setEnabled(false);
  }

  dispose(): void {
    this.root?.dispose();
  }
}
